import { initialSettings, applyVisualPreset, type Settings } from './config';
import { IS_LITE } from './edition';
export const SETTINGS_KEY = IS_LITE
  ? 'lite-blocksandbox.browser.settings.v1'
  : 'blocksandbox.browser.settings.v1';
type Store = Pick<Storage, 'getItem' | 'setItem'>;
const ranges: Partial<Record<keyof Settings, readonly [number, number]>> = {
  scale: [0.5, 1.5],
  density: [0, 2000],
  viewDistance: [90, 360],
  sceneryDistance: [600, 1800],
  physicsRadius: [60, 300],
  volume: [0, 1],
  effectsVolume: [0, 1],
  musicVolume: [0, 1],
  ambienceVolume: [0, 1],
  sirenVolume: [0, 1],
  sensitivity: [0.3, 2],
  c4Strength: [0.5, 3],
  airstrikeStrength: [0, 10],
};
const choices: Partial<Record<keyof Settings, readonly (string | number)[]>> = {
  quality: ['low', 'medium', 'high', 'custom', 'auto'],
  shadows: [0, 512, 1024, 2048],
  effects: [0, 1, 2],
  detail: [0, 1, 2],
  c4Footprint: [1, 2.5, 10, 25],
  railHeadway: [60, 90, 150],
};
export function loadPreferences(reduced = false, store?: Store): Settings {
  const result = initialSettings();
  result.reduced = reduced;
  try {
    const saved = JSON.parse((store ?? localStorage).getItem(SETTINGS_KEY) ?? 'null');
    if (saved?.version !== 1 || !saved.settings || typeof saved.settings !== 'object') return result;
    for (const key of Object.keys(result) as (keyof Settings)[]) {
      const value = saved.settings[key],
        range = ranges[key],
        allowed = choices[key];
      if (
        range
          ? typeof value === 'number' && Number.isFinite(value) && value >= range[0] && value <= range[1]
          : allowed
            ? allowed.includes(value)
            : typeof value === 'boolean'
      )
        Object.assign(result, { [key]: value });
    }
    if (['low', 'medium', 'high'].includes(result.quality)) {
      const preset = { ...result };
      applyVisualPreset(preset, result.quality as 'low' | 'medium' | 'high');
      if (
        ['scale', 'shadows', 'detail', 'effects', 'viewDistance', 'sceneryDistance'].some(
          (k) => result[k as keyof Settings] !== preset[k as keyof Settings],
        )
      )
        result.quality = 'custom';
    }
  } catch {
    /* Storage denial or malformed old data keeps finite working defaults. */
  }
  return result;
}
export function savePreferences(settings: Settings, store?: Store): void {
  try {
    (store ?? localStorage).setItem(SETTINGS_KEY, JSON.stringify({ version: 1, settings }));
  } catch {
    /* A private context may not permit persistence. */
  }
}
